import type { ActivityDefinition } from "@medplum/fhirtypes";
import {
  getTRPCClient,
  memberKey,
  ORDER_SET_ENTITY_ID_SYSTEM,
  SCHEDULE_ENTITY_ID_SYSTEM,
  VISIT_ASSIGNMENT_MEMBER_ID_SYSTEM,
  VISIT_ENTITY_ID_SYSTEM,
  VISIT_EXECUTION_MEMBER_ID_SYSTEM,
  VISIT_KIND_SYSTEM,
} from "@repo/api";
import {
  type BookProtocolVisitInput,
  type BookProtocolVisitParams,
  bookProtocolVisitInputSchema,
} from "@repo/api/schemas";
import { DEFAULT_VISIT_EXECUTION_MEMBER_ID } from "@repo/domain";
import { ACT_CODE_SYSTEM } from "@repo/fhir-systems";
import { ORDER_KIND_SYSTEM } from "@repo/fhir-systems/code-systems";
import { ENCOUNTER_CLASS_URL, ORDER_ID_URL, ORDER_SOURCE_OCCURRENCE_URL } from "@repo/fhir-systems/extensions";
import { defineWrite } from "../actions/define-write";
import { getDataStoreClient } from "../core/provider";
import { DomainResultError } from "../core/write-errors";
import type {
  AppointmentRow,
  EncounterRow,
  ObservationRow,
  RequestGroupRow,
  ServiceRequestRow,
  SlotRow,
} from "../fhir/types";
import { mintExamIds } from "./_caller-identities";
import { optimisticCaseReference } from "./_case-reference";
import { optimisticProtocolExams } from "./_exam-optimistic";
import { optimisticPatientReference } from "./_patient-reference";
import type { ProtocolBookingDto } from "./protocol-booking";

interface WritableCollection<T extends object> {
  insert(value: T): unknown;
}

export interface ProtocolBookingCollections {
  appointment: WritableCollection<AppointmentRow>;
  encounter: WritableCollection<EncounterRow>;
  bookedSlot: WritableCollection<SlotRow>;
  slot: WritableCollection<SlotRow>;
  requestGroup: WritableCollection<RequestGroupRow>;
  serviceRequest: WritableCollection<ServiceRequestRow>;
  exams: WritableCollection<ObservationRow>;
}

export type BookProtocolVisitActionParams = BookProtocolVisitParams & {
  protocol: ProtocolBookingDto;
};

export interface ProtocolBookingActionDependencies {
  collections: () => ProtocolBookingCollections;
  definitions: () => readonly ActivityDefinition[];
  persist: Parameters<typeof defineWrite<BookProtocolVisitParams>>[0]["persist"];
}

function optimisticGraph(input: BookProtocolVisitInput, protocol: ProtocolBookingDto, currentCaseId: string) {
  const visitId = input.id;
  const orderSetId = input.orderSetId;
  const orderSetReference = { identifier: { system: ORDER_SET_ENTITY_ID_SYSTEM, value: orderSetId } };
  const slots: SlotRow[] = input.candidate.placements.map((placement, index) => {
    const assignmentId = `assignment-${index + 1}`;
    return {
      resourceType: "Slot",
      identifier: [
        { system: VISIT_ASSIGNMENT_MEMBER_ID_SYSTEM, value: assignmentId },
        { system: VISIT_ENTITY_ID_SYSTEM, value: visitId },
      ],
      status: "busy",
      schedule: { identifier: { system: SCHEDULE_ENTITY_ID_SYSTEM, value: placement.scheduleId } },
      scheduleDomainId: placement.scheduleId,
      scheduleJoinKey: placement.scheduleId,
      slotKey: memberKey(visitId, assignmentId),
      start: placement.start,
      end: placement.end,
    };
  });
  const orders = protocol.steps.flatMap((step) =>
    step.orders.map(
      (order): ServiceRequestRow => ({
        resourceType: "ServiceRequest",
        status: "draft",
        intent: "order",
        category: [{ coding: [{ system: ORDER_KIND_SYSTEM, code: "procedure" }] }],
        subject: optimisticPatientReference(input.patientId),
        basedOn: [orderSetReference],
        instantiatesCanonical: [order.definitionCanonical],
        orderSetId,
        orderSetJoinKey: orderSetId,
        orderId: order.orderId,
        definitionCanonical: order.definitionCanonical,
        extension: [
          { url: ORDER_ID_URL, valueString: order.orderId },
          { url: ORDER_SOURCE_OCCURRENCE_URL, valueString: order.sourceOccurrenceId },
        ],
      }),
    ),
  );
  const slotKeys = slots.map((slot) => slot.slotKey);
  const appointment: AppointmentRow = {
    resourceType: "Appointment",
    identifier: [{ system: VISIT_ENTITY_ID_SYSTEM, value: visitId }],
    domainId: visitId,
    patientId: input.patientId,
    orderSetId,
    orderSetJoinKey: orderSetId,
    slotKeys,
    firstSlotJoinKey: slotKeys[0] ?? `_noslot_${visitId}`,
    status: "booked",
    start: input.candidate.start,
    end: input.candidate.end,
    appointmentType: { coding: [{ system: VISIT_KIND_SYSTEM, code: "standalone" }] },
    participant: [
      { actor: optimisticPatientReference(input.patientId), status: "accepted" },
      ...input.candidate.placements.map((placement) => ({
        actor: { reference: `${placement.resourceType}/${placement.resourceId}` },
        status: "accepted" as const,
      })),
    ],
    slot: slots.map((slot) => ({
      identifier: { system: VISIT_ASSIGNMENT_MEMBER_ID_SYSTEM, value: slot.identifier?.[0]?.value },
    })),
    supportingInformation: [optimisticCaseReference(currentCaseId), orderSetReference],
    extension: [
      {
        url: ENCOUNTER_CLASS_URL,
        valueCoding: { system: ACT_CODE_SYSTEM, code: "AMB" },
      },
    ],
  };
  const encounter: EncounterRow = {
    resourceType: "Encounter",
    identifier: [{ system: VISIT_EXECUTION_MEMBER_ID_SYSTEM, value: DEFAULT_VISIT_EXECUTION_MEMBER_ID }],
    visitId,
    visitJoinKey: visitId,
    status: "planned",
    class: { system: ACT_CODE_SYSTEM, code: "AMB" },
    subject: optimisticPatientReference(input.patientId),
    appointment: [{ identifier: { system: VISIT_ENTITY_ID_SYSTEM, value: visitId } }],
    episodeOfCare: [optimisticCaseReference(currentCaseId)],
  };
  const requestGroup: RequestGroupRow = {
    resourceType: "RequestGroup",
    identifier: [{ system: ORDER_SET_ENTITY_ID_SYSTEM, value: orderSetId }],
    domainId: orderSetId,
    status: "active",
    intent: "order",
    subject: optimisticPatientReference(input.patientId),
    instantiatesCanonical: [`${protocol.protocolUrl}|${protocol.protocolVersion}`],
    isRedesignedOrderSet: true,
    patientId: input.patientId,
    protocolCanonical: `${protocol.protocolUrl}|${protocol.protocolVersion}`,
    protocolUrl: protocol.protocolUrl,
    protocolVersion: protocol.protocolVersion,
    memberCount: orders.length,
    action: protocol.steps.map((step) => ({
      action: step.orders.map((order) => ({
        resource: { identifier: { system: ORDER_ID_URL, value: order.orderId } },
      })),
    })),
  };
  return { appointment, encounter, slots, requestGroup, orders };
}

export function createBookProtocolVisitAction(dependencies: ProtocolBookingActionDependencies) {
  return defineWrite<BookProtocolVisitActionParams>({
    optimistic: (params) => {
      const input = bookProtocolVisitInputSchema.parse(params);
      if (
        params.protocol.protocolUrl !== input.protocolUrl ||
        params.protocol.protocolVersion !== input.protocolVersion
      ) {
        throw new Error("The optimistic protocol projection does not match the selected protocol pin");
      }
      const graph = optimisticGraph(input, params.protocol, params.currentCaseId);
      const collections = dependencies.collections();
      collections.appointment.insert(graph.appointment);
      collections.encounter.insert(graph.encounter);
      for (const slot of graph.slots) {
        collections.bookedSlot.insert(slot);
        collections.slot.insert(slot);
      }
      collections.requestGroup.insert(graph.requestGroup);
      for (const order of graph.orders) collections.serviceRequest.insert(order);
      for (const exam of optimisticProtocolExams(input, params.protocol.examUrls, dependencies.definitions()))
        collections.exams.insert(exam);
    },
    persist: async ({ protocol: _protocol, ...params }, options) => {
      await dependencies.persist(params, options);
    },
  });
}

const bookProtocolVisitAction = createBookProtocolVisitAction({
  definitions: () => [...getDataStoreClient().getCollection("activityDefinition").values()],
  collections: () => {
    const client = getDataStoreClient();
    return {
      appointment: client.getCollection("appointment"),
      encounter: client.getCollection("encounter"),
      bookedSlot: client.getCollection("booked-slot"),
      slot: client.getCollection("slot"),
      requestGroup: client.getCollection("requestGroup"),
      serviceRequest: client.getCollection("serviceRequest"),
      exams: client.getCollection("observation"),
    };
  },
  persist: async (params, options) => {
    const result = await getTRPCClient().booking.bookProtocolVisit.mutate(params, options);
    if (!result.success) throw new DomainResultError(result.error.message);
  },
});

export function bookProtocolVisit(params: Omit<BookProtocolVisitActionParams, "examIds">) {
  return bookProtocolVisitAction({ ...params, examIds: mintExamIds() });
}
